/**
 * Shared upgrade-prompt UI — rendered whenever an API call fails with the
 * machine-readable entitlement error codes the backend returns (see
 * backend/src/services/entitlements.ts + backend/src/errors.ts):
 *
 *   ENTITLEMENT_REQUIRED    — the whole feature needs a higher plan
 *   BOTTLE_LIMIT_REACHED    — free-plan 50-bottle cap hit
 *   HOUSEHOLD_LIMIT_REACHED — free-plan household-size cap hit
 *
 * Used on vinliste/varetelling/prising/bestilling pages (full-page variant)
 * and the add-bottle flow (inline variant). Never shows a raw error message
 * for these codes — always this consistent, actionable card instead.
 */
import { Link } from 'react-router-dom';
import { ApiError } from '@/lib/api/client';
import { Badge, Button, EmptyState, Icon } from '@/design-system';
import { planLabel, type PlanId } from '@/lib/entitlements';

export type EntitlementErrorCode = 'ENTITLEMENT_REQUIRED' | 'BOTTLE_LIMIT_REACHED' | 'HOUSEHOLD_LIMIT_REACHED';

const ENTITLEMENT_CODES: readonly string[] = ['ENTITLEMENT_REQUIRED', 'BOTTLE_LIMIT_REACHED', 'HOUSEHOLD_LIMIT_REACHED'];

export function isEntitlementError(error: unknown): error is ApiError {
  return error instanceof ApiError && ENTITLEMENT_CODES.includes(error.code);
}

const TITLE_BY_CODE: Record<EntitlementErrorCode, string> = {
  ENTITLEMENT_REQUIRED: 'Krever oppgradert plan',
  BOTTLE_LIMIT_REACHED: 'Flaskegrensen er nådd',
  HOUSEHOLD_LIMIT_REACHED: 'Husholdningen er full',
};

/**
 * Full-page variant — swap in for a page's normal content when its
 * primary query failed with an entitlement error (vinliste, varetelling,
 * prising, bestilling).
 */
export function UpgradeGate({ error, featureLabel }: { error: unknown; featureLabel?: string }) {
  if (!isEntitlementError(error)) return null;
  const code = error.code as EntitlementErrorCode;
  const details = (error.details ?? {}) as { currentPlan?: string; limit?: number };
  const title = TITLE_BY_CODE[code] ?? 'Krever oppgradert plan';

  return (
    <EmptyState
      icon="lock"
      title={featureLabel ? `${featureLabel} krever en oppgradert plan` : title}
      description={
        <span>
          {error.message}
          {details.currentPlan && (
            <>
              {' '}
              Nåværende plan: <strong>{planLabel(details.currentPlan)}</strong>.
            </>
          )}
        </span>
      }
      action={
        <Link to="/abonnement">
          <Button variant="primary" iconRight="arrow-right">
            Se planer og oppgrader
          </Button>
        </Link>
      }
    />
  );
}

/**
 * Compact inline variant — for flows that shouldn't lose their whole page
 * to an upgrade prompt (e.g. AddBottleDrawer hitting the bottle cap).
 */
export function UpgradeInlineNotice({ error }: { error: unknown }) {
  if (!isEntitlementError(error)) return null;
  return (
    <div className="flex items-start gap-3 rounded-md border border-candle/40 bg-candle/5 p-3 text-sm">
      <Icon name="lock" size={16} className="mt-0.5 shrink-0 text-candle" />
      <div className="grid gap-1.5">
        <p className="text-parchment">{error.message}</p>
        <Link to="/abonnement" className="text-xs font-semibold uppercase tracking-wider text-candle hover:underline">
          Se planer og oppgrader →
        </Link>
      </div>
    </div>
  );
}

/** Small "Pro"/"Pluss" badge for nav items locked on the current plan. */
export function PlanBadge({ plan }: { plan: 'pluss' | 'pro' }) {
  return (
    <Badge tone="gold" className="ml-auto shrink-0 !px-1.5 !py-0 text-[9px]">
      {planLabel(plan as PlanId)}
    </Badge>
  );
}
